// src/admin/pages/AdminSettings.jsx
import { useState, useEffect } from "react";
import AdminLayout from "../layout/AdminLayout";
import { API_BASE_URL } from "../../config/api";
import { adminAuthHeader } from "../../utils/authHeader";

const AdminSettings = () => {
  const [form,       setForm]       = useState({ upiId: "", accountName: "", qrCodeUrl: "", note: "", isActive: true, amount: 300, gstEnabled: false, gstPercent: 18 });
  const [loading,    setLoading]    = useState(true);
  const [saving,     setSaving]     = useState(false);
  const [validating, setValidating] = useState(false);
  const [msg,        setMsg]        = useState("");
  const [upiCheck,   setUpiCheck]   = useState(null);

  // ── Razorpay state ──────────────────────────────────────────
  const [rzpForm,      setRzpForm]      = useState({ keyId: "", keySecret: "", isActive: true, mode: "test" });
  const [rzpSaving,    setRzpSaving]    = useState(false);
  const [rzpMsg,       setRzpMsg]       = useState("");
  const [rzpHasSecret, setRzpHasSecret] = useState(false);
  const [showSecret,   setShowSecret]   = useState(false);
  const [showManual,   setShowManual]   = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [upiRes, rzpRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/settings/upi`,      { headers: adminAuthHeader() }),
        fetch(`${API_BASE_URL}/api/settings/razorpay`, { headers: adminAuthHeader() }),
      ]);
      const upiData = await upiRes.json();
      const rzpData = await rzpRes.json();

      setForm({
        upiId:       upiData.upiId       || "",
        accountName: upiData.accountName || "",
        qrCodeUrl:   upiData.qrCodeUrl   || "",
        note:        upiData.note        || "",
        isActive:    upiData.isActive !== false,
        amount:      upiData.amount      || 300,
        gstEnabled:  upiData.gstEnabled  || false,
        gstPercent:  upiData.gstPercent  || 18,
      });
      setRzpForm({
        keyId:    rzpData.keyId    || "",
        keySecret: "",  // never pre-filled — must re-enter to change
        isActive: rzpData.isActive !== false,
        mode:     rzpData.mode     || "test",
      });
      setRzpHasSecret(rzpData.hasSecret || false);
    } catch { }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const validateUPI = async () => {
    if (!form.upiId.trim()) { setUpiCheck({ valid: false, message: "Enter a UPI ID first" }); return; }
    setValidating(true); setUpiCheck(null);
    try {
      const res  = await fetch(`${API_BASE_URL}/api/settings/upi/validate`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...adminAuthHeader() },
        body: JSON.stringify({ upiId: form.upiId }),
      });
      const data = await res.json();
      setUpiCheck(data);
    } catch { setUpiCheck({ valid: false, message: "Validation failed" }); }
    setValidating(false);
  };

  // Save only the amount (no UPI validation needed)
  const handleSaveAmount = async () => {
    if (!form.amount || form.amount < 1) { setMsg("❌ Amount must be at least ₹1"); return; }
    setSaving(true); setMsg("");
    try {
      const res  = await fetch(`${API_BASE_URL}/api/settings/upi`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...adminAuthHeader() },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (res.ok) { setMsg("✅ Amount saved"); load(); }
      else        { setMsg("❌ " + (data.message || "Save failed")); }
    } catch { setMsg("❌ Save failed"); }
    setSaving(false);
  };

  const handleSave = async () => {
    if (!form.upiId.trim())       { setMsg("❌ UPI ID is required"); return; }
    if (!form.accountName.trim()) { setMsg("❌ Account name is required"); return; }
    if (!form.amount || form.amount < 1) { setMsg("❌ Amount must be at least ₹1"); return; }
    setSaving(true); setMsg("");
    try {
      const res  = await fetch(`${API_BASE_URL}/api/settings/upi`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...adminAuthHeader() },
        body: JSON.stringify(form), // ← amount is included in form
      });
      const data = await res.json();
      if (res.ok) { setMsg("✅ " + data.message); load(); }
      else        { setMsg("❌ " + data.message); }
    } catch { setMsg("❌ Save failed"); }
    setSaving(false);
  };

  const handleRazorpaySave = async () => {
    if (!rzpForm.keyId.trim())                       { setRzpMsg("❌ Key ID is required"); return; }
    if (!rzpForm.keyId.trim().startsWith("rzp_"))    { setRzpMsg("❌ Key ID must start with rzp_test_ or rzp_live_"); return; }
    if (!rzpHasSecret && !rzpForm.keySecret.trim())  { setRzpMsg("❌ Key Secret is required for first-time setup"); return; }

    setRzpSaving(true); setRzpMsg("");
    try {
      const res  = await fetch(`${API_BASE_URL}/api/settings/razorpay`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...adminAuthHeader() },
        body: JSON.stringify(rzpForm),
      });
      const data = await res.json();
      if (res.ok) {
        setRzpMsg("✅ " + data.message);
        setRzpForm(f => ({ ...f, keySecret: "" }));
        load();
      } else {
        setRzpMsg("❌ " + data.message);
      }
    } catch { setRzpMsg("❌ Save failed. Try again."); }
    setRzpSaving(false);
  };

  // ── Dynamic amount in all UPI links ─────────────────────────
  const amt      = form.amount || 300;
  const upiLink  = `upi://pay?pa=${form.upiId}&pn=${encodeURIComponent(form.accountName)}&am=${amt}&cu=INR&tn=FDS+Subscription`;
  const gpayLink = `gpay://upi/pay?pa=${form.upiId}&pn=${encodeURIComponent(form.accountName)}&am=${amt}&cu=INR`;
  const qrUrl    = form.upiId
    ? `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(upiLink)}`
    : "";

  if (loading) return (
    <AdminLayout>
      <div style={{ textAlign: "center", padding: 60, color: "#a3aed0" }}>Loading settings...</div>
    </AdminLayout>
  );

  return (
    <AdminLayout>
      <div className="admin-header">
        <p className="admin-welcome">Configuration</p>
        <h2 className="admin-title">⚙️ Payment Settings</h2>
      </div>

      {/* ══════════════════════════════════════════════════════
          1. SUBSCRIPTION AMOUNT — shared by all payment methods
         ══════════════════════════════════════════════════════ */}
      <div style={{ maxWidth: 900, marginBottom: 24 }}>
        <div style={{ background: "#fff", borderRadius: 16, padding: 24, boxShadow: "0 2px 10px rgba(0,0,0,0.05)" }}>

          {/* ── Row 1: title + inputs + save ── */}
          <div style={{ display: "flex", alignItems: "flex-end", gap: 16, flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 180 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#1b2559", marginBottom: 2 }}>💰 Subscription Amount</div>
              <div style={{ fontSize: 11, color: "#a3aed0" }}>Used by Razorpay and UPI payments. Change once — applies everywhere.</div>
            </div>

            {/* Base price input */}
            <div>
              <div style={{ fontSize: 10, color: "#a3aed0", fontWeight: 700, marginBottom: 4, letterSpacing: 0.5 }}>BASE PRICE</div>
              <div style={{ position: "relative" }}>
                <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", fontWeight: 700, color: "#4318FF", fontSize: 15 }}>₹</span>
                <input
                  type="number" min="1"
                  style={{ width: 110, padding: "10px 10px 10px 26px", borderRadius: 10, border: "2px solid #4318FF40", fontSize: 17, fontWeight: 800, color: "#1b2559", outline: "none" }}
                  value={form.amount}
                  onChange={e => setForm(f => ({ ...f, amount: parseInt(e.target.value) || 0 }))}
                />
              </div>
            </div>

            {/* GST toggle + percent */}
            <div>
              <div style={{ fontSize: 10, color: "#a3aed0", fontWeight: 700, marginBottom: 4, letterSpacing: 0.5 }}>GST</div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 14px", border: `2px solid ${form.gstEnabled ? "#16a34a60" : "#e0e5f2"}`, borderRadius: 10, background: form.gstEnabled ? "#f0fdf4" : "#fafafa", height: 42, boxSizing: "border-box" }}>
                <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", margin: 0, userSelect: "none" }}>
                  <input type="checkbox" checked={form.gstEnabled}
                    onChange={e => setForm(f => ({ ...f, gstEnabled: e.target.checked }))}
                    style={{ width: 15, height: 15, cursor: "pointer" }} />
                  <span style={{ fontSize: 13, fontWeight: 700, color: form.gstEnabled ? "#16a34a" : "#6b7280" }}>
                    {form.gstEnabled ? "GST ON" : "GST OFF"}
                  </span>
                </label>
                {form.gstEnabled && (
                  <>
                    <div style={{ width: 1, height: 18, background: "#16a34a40" }} />
                    <input
                      type="number" min="0" max="100" step="0.5"
                      style={{ width: 52, padding: "2px 6px", borderRadius: 6, border: "1.5px solid #16a34a60", fontSize: 14, fontWeight: 800, color: "#16a34a", outline: "none", textAlign: "center" }}
                      value={form.gstPercent}
                      onChange={e => setForm(f => ({ ...f, gstPercent: parseFloat(e.target.value) || 0 }))}
                    />
                    <span style={{ fontSize: 13, fontWeight: 700, color: "#16a34a" }}>%</span>
                  </>
                )}
              </div>
            </div>

            <button onClick={handleSaveAmount} disabled={saving}
              style={{ padding: "10px 24px", borderRadius: 10, border: "none", cursor: "pointer", background: "#4318FF", color: "#fff", fontWeight: 700, fontSize: 13, height: 42, opacity: saving ? 0.7 : 1 }}>
              {saving ? "Saving..." : "Save"}
            </button>
          </div>

          {/* ── Row 2: GST breakdown (always visible when GST is on) ── */}
          {form.gstEnabled && (() => {
            const gstAmt   = Math.round(form.amount * form.gstPercent / 100);
            const total    = form.amount + gstAmt;
            return (
              <div style={{ marginTop: 16, display: "flex", alignItems: "center", gap: 12, background: "#f0fdf4", border: "1.5px solid #16a34a30", borderRadius: 12, padding: "12px 16px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#374151", flex: 1, flexWrap: "wrap", gap: 6 }}>
                  <span style={{ background: "#fff", border: "1.5px solid #e0e5f2", borderRadius: 8, padding: "4px 10px", fontWeight: 700 }}>₹{form.amount}</span>
                  <span style={{ color: "#6b7280" }}>+</span>
                  <span style={{ background: "#fff", border: "1.5px solid #e0e5f2", borderRadius: 8, padding: "4px 10px", fontWeight: 700 }}>GST {form.gstPercent}% = ₹{gstAmt}</span>
                  <span style={{ color: "#6b7280" }}>=</span>
                </div>
                <div style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                  <div style={{ fontSize: 11, color: "#16a34a", fontWeight: 700, marginBottom: 1 }}>TOTAL CHARGED TO OWNER</div>
                  <div style={{ fontSize: 26, fontWeight: 900, color: "#16a34a" }}>₹{total}</div>
                </div>
              </div>
            );
          })()}

          {/* ── Save message ── */}
          {msg && (
            <div style={{ marginTop: 12, padding: "8px 14px", borderRadius: 8, fontWeight: 600, fontSize: 13,
              background: msg.startsWith("✅") ? "#dcfce7" : "#fee2e2",
              color:      msg.startsWith("✅") ? "#16a34a"  : "#dc2626" }}>
              {msg}
            </div>
          )}
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════
          2. RAZORPAY — primary online payment
         ══════════════════════════════════════════════════════ */}
      <div style={{ maxWidth: 900, marginBottom: 24 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
          <div style={{ width: 4, height: 22, background: "#16a34a", borderRadius: 4 }} />
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: "#1b2559" }}>Razorpay — Online Payment</h3>
          <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 10px", borderRadius: 20,
            background: rzpForm.isActive ? "#dcfce7" : "#fee2e2",
            color:      rzpForm.isActive ? "#16a34a"  : "#dc2626" }}>
            {rzpForm.isActive ? "Active" : "Disabled"}
          </span>
          <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 10px", borderRadius: 20,
            background: rzpForm.keyId.startsWith("rzp_live_") ? "#dcfce7" : "#fef3c7",
            color:      rzpForm.keyId.startsWith("rzp_live_") ? "#16a34a"  : "#92400e" }}>
            {rzpForm.keyId.startsWith("rzp_live_") ? "🟢 LIVE" : "🟡 TEST"}
          </span>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
          {/* Form */}
          <div style={{ background: "#fff", borderRadius: 16, padding: 24, boxShadow: "0 2px 10px rgba(0,0,0,0.05)", border: "1.5px solid #16a34a20" }}>

            <label style={lbl}>Key ID * <span style={{ fontWeight: 400, color: "#a3aed0" }}>(rzp_test_... or rzp_live_...)</span></label>
            <input style={inp} placeholder="rzp_test_xxxxxxxxxxxxxxxxxxxx"
              value={rzpForm.keyId}
              onChange={e => setRzpForm(f => ({ ...f, keyId: e.target.value }))} />

            <label style={lbl}>
              Key Secret *
              {rzpHasSecret && <span style={{ fontWeight: 400, color: "#16a34a", marginLeft: 8 }}>✅ Saved — leave blank to keep</span>}
            </label>
            <div style={{ position: "relative", marginBottom: 14 }}>
              <input
                style={{ ...inp, marginBottom: 0, paddingRight: 70 }}
                type={showSecret ? "text" : "password"}
                placeholder={rzpHasSecret ? "Leave blank to keep existing" : "Enter Key Secret"}
                value={rzpForm.keySecret}
                onChange={e => setRzpForm(f => ({ ...f, keySecret: e.target.value }))}
              />
              <button type="button" onClick={() => setShowSecret(s => !s)}
                style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
                  background: "none", border: "none", cursor: "pointer", fontSize: 11, color: "#6b7280", fontWeight: 600 }}>
                {showSecret ? "Hide" : "Show"}
              </button>
            </div>

            <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", marginBottom: 18, fontSize: 13 }}>
              <input type="checkbox" checked={rzpForm.isActive}
                onChange={e => setRzpForm(f => ({ ...f, isActive: e.target.checked }))}
                style={{ width: 16, height: 16 }} />
              <span style={{ color: "#374151" }}>Show "Pay Online with Razorpay" button to owners</span>
            </label>

            {rzpMsg && (
              <div style={{ padding: "10px 14px", borderRadius: 8, marginBottom: 14, fontWeight: 600, fontSize: 13,
                background: rzpMsg.startsWith("✅") ? "#dcfce7" : "#fee2e2",
                color:      rzpMsg.startsWith("✅") ? "#16a34a"  : "#dc2626" }}>
                {rzpMsg}
              </div>
            )}

            <button onClick={handleRazorpaySave} disabled={rzpSaving}
              style={{ width: "100%", padding: 13, borderRadius: 10, border: "none", cursor: "pointer",
                background: "#16a34a", color: "#fff", fontWeight: 800, fontSize: 14, opacity: rzpSaving ? 0.7 : 1 }}>
              {rzpSaving ? "Saving..." : "💾 Save Razorpay Settings"}
            </button>
          </div>

          {/* Info */}
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{ background: "#fff", borderRadius: 16, padding: 18, boxShadow: "0 2px 10px rgba(0,0,0,0.05)" }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#1b2559", marginBottom: 10 }}>📊 Status</div>
              {[
                ["Gateway",    rzpForm.isActive ? "✅ Active" : "❌ Disabled",   rzpForm.isActive ? "#16a34a" : "#dc2626"],
                ["Mode",       rzpForm.keyId.startsWith("rzp_live_") ? "🟢 Live" : "🟡 Test", rzpForm.keyId.startsWith("rzp_live_") ? "#16a34a" : "#92400e"],
                ["Key Secret", rzpHasSecret ? "✅ Saved" : "❌ Not set",         rzpHasSecret ? "#16a34a" : "#dc2626"],
              ].map(([label, value, color]) => (
                <div key={label} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 6 }}>
                  <span style={{ color: "#6b7280" }}>{label}</span>
                  <span style={{ fontWeight: 700, color }}>{value}</span>
                </div>
              ))}
            </div>

            <div style={{ background: "#ede9fe", borderRadius: 16, padding: 18, border: "1.5px solid #4318FF20" }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#4318FF", marginBottom: 8 }}>📋 How to get API keys</div>
              <ol style={{ margin: 0, paddingLeft: 16, fontSize: 12, color: "#4318FF", lineHeight: 2 }}>
                <li>Login to <b>razorpay.com</b> → Dashboard</li>
                <li>Settings → <b>API Keys</b></li>
                <li>Generate Test Key (or Live Key after KYC)</li>
                <li>Paste Key ID + Key Secret here → Save</li>
              </ol>
            </div>

            <div style={{ background: "#fffbeb", borderRadius: 16, padding: 18, border: "1.5px solid #fde68a" }}>
              <div style={{ fontSize: 12, color: "#92400e", lineHeight: 1.9 }}>
                <b>Test keys</b> (rzp_test_...): for testing, no real money<br/>
                <b>Live keys</b> (rzp_live_...): real payments — needs KYC<br/>
                If owners see "payment declined" → switch to Live keys
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════
          3. MANUAL PAYMENT — UPI / Cash (secondary / backup)
         ══════════════════════════════════════════════════════ */}
      <div style={{ maxWidth: 900 }}>
        <button onClick={() => setShowManual(s => !s)}
          style={{ display: "flex", alignItems: "center", gap: 10, background: "none", border: "none", cursor: "pointer", padding: 0, marginBottom: showManual ? 14 : 0 }}>
          <div style={{ width: 4, height: 22, background: "#6b7280", borderRadius: 4 }} />
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: "#6b7280" }}>Manual Payment — UPI / Cash</h3>
          <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 10px", borderRadius: 20, background: "#f4f7fe", color: "#6b7280" }}>
            Backup / Offline
          </span>
          <span style={{ fontSize: 13, color: "#a3aed0", marginLeft: 4 }}>{showManual ? "▲ Hide" : "▼ Show"}</span>
        </button>
        {!showManual && (
          <p style={{ fontSize: 12, color: "#a3aed0", margin: "6px 0 0 14px" }}>
            For owners who prefer cash or manual UPI. Requires admin to manually verify and activate.
          </p>
        )}

        {showManual && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>

            {/* UPI Form */}
            <div style={{ background: "#fff", borderRadius: 16, padding: 24, boxShadow: "0 2px 10px rgba(0,0,0,0.05)", border: "1.5px solid #e0e5f2" }}>
              <h3 style={{ margin: "0 0 16px", color: "#1b2559", fontSize: 14 }}>💳 UPI / Cash Details</h3>
              <p style={{ fontSize: 12, color: "#6b7280", marginBottom: 16, lineHeight: 1.7 }}>
                These details appear on the owner payment page as a manual payment option. Admin must manually verify and activate after payment.
              </p>

              <label style={lbl}>Account Holder Name *</label>
              <input style={inp} placeholder="e.g. Umasankar Srinivas"
                value={form.accountName}
                onChange={e => setForm(f => ({ ...f, accountName: e.target.value }))} />

              <label style={lbl}>UPI ID *</label>
              <div style={{ display: "flex", gap: 8, marginBottom: 6 }}>
                <input style={{ ...inp, marginBottom: 0, flex: 1 }}
                  placeholder="e.g. 9876543210@ybl"
                  value={form.upiId}
                  onChange={e => { setForm(f => ({ ...f, upiId: e.target.value })); setUpiCheck(null); }} />
                <button onClick={validateUPI} disabled={validating}
                  style={{ padding: "9px 14px", borderRadius: 8, border: "none", background: "#4318FF", color: "#fff", fontWeight: 700, fontSize: 12, cursor: "pointer", whiteSpace: "nowrap" }}>
                  {validating ? "..." : "✓ Validate"}
                </button>
              </div>
              {upiCheck && (
                <div style={{ padding: "8px 12px", borderRadius: 8, marginBottom: 12, fontSize: 12, fontWeight: 600,
                  background: upiCheck.valid ? "#dcfce7" : "#fee2e2",
                  color:      upiCheck.valid ? "#16a34a"  : "#dc2626" }}>
                  {upiCheck.message}
                </div>
              )}

              <label style={lbl}>Payment Note (shown to owners)</label>
              <input style={inp} placeholder="e.g. Pay ₹499 for FDS Standard Plan"
                value={form.note}
                onChange={e => setForm(f => ({ ...f, note: e.target.value }))} />

              <label style={lbl}>Custom QR Code URL (optional)</label>
              <input style={inp} placeholder="Leave empty to auto-generate"
                value={form.qrCodeUrl}
                onChange={e => setForm(f => ({ ...f, qrCodeUrl: e.target.value }))} />

              <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", marginBottom: 16, fontSize: 13 }}>
                <input type="checkbox" checked={form.isActive}
                  onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))}
                  style={{ width: 16, height: 16 }} />
                <span style={{ color: "#374151" }}>Show UPI/Cash option to owners</span>
              </label>

              <button onClick={handleSave} disabled={saving}
                style={{ width: "100%", padding: 13, borderRadius: 10, border: "none", cursor: "pointer",
                  background: "#4318FF", color: "#fff", fontWeight: 800, fontSize: 14, opacity: saving ? 0.7 : 1 }}>
                {saving ? "Saving..." : "💾 Save UPI Settings"}
              </button>
            </div>

            {/* QR Preview */}
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div style={{ background: "#fff", borderRadius: 16, padding: 20, boxShadow: "0 2px 10px rgba(0,0,0,0.05)", textAlign: "center" }}>
                <h3 style={{ margin: "0 0 14px", color: "#1b2559", fontSize: 14 }}>📱 QR Preview</h3>
                {form.upiId ? (
                  <>
                    <img src={form.qrCodeUrl || qrUrl} alt="UPI QR"
                      style={{ width: 160, height: 160, border: "1.5px solid #e0e5f2", borderRadius: 12, padding: 8 }}
                      onError={e => e.target.style.display = "none"} />
                    <p style={{ fontSize: 13, fontWeight: 700, color: "#1b2559", margin: "8px 0 0" }}>{form.accountName}</p>
                    <p style={{ fontSize: 12, color: "#6b7280", margin: "2px 0 0" }}>{form.upiId}</p>
                    <div style={{ marginTop: 10, padding: "6px 12px", background: "#ede9fe", borderRadius: 8, fontSize: 14, color: "#4318FF", fontWeight: 800 }}>
                      ₹{form.amount || 300}
                    </div>
                  </>
                ) : (
                  <div style={{ padding: 24, color: "#a3aed0", fontSize: 13 }}>Enter UPI ID to preview</div>
                )}
              </div>

              {form.upiId && (
                <div style={{ background: "#fff", borderRadius: 16, padding: 18, boxShadow: "0 2px 10px rgba(0,0,0,0.05)" }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#1b2559", marginBottom: 10 }}>🔗 Payment Links</div>
                  <div style={{ fontSize: 11, background: "#f4f7fe", borderRadius: 8, padding: 10, wordBreak: "break-all", color: "#4318FF", marginBottom: 8 }}>
                    <b>GPay:</b><br/>{gpayLink}
                  </div>
                  <div style={{ fontSize: 11, background: "#f4f7fe", borderRadius: 8, padding: 10, wordBreak: "break-all", color: "#4318FF" }}>
                    <b>Universal:</b><br/>{upiLink}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

    </AdminLayout>
  );
};

const lbl = { display: "block", fontSize: 12, fontWeight: 700, color: "#1b2559", marginBottom: 5 };
const inp = { width: "100%", padding: "9px 12px", borderRadius: 8, border: "1.5px solid #e0e5f2", marginBottom: 14, fontSize: 13, outline: "none", boxSizing: "border-box" };

export default AdminSettings;